import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const VAEN_COLOR_ALIASES = {
  off_white: ['off_white', 'white'],
  ivory: ['ivory'],
  sand: ['sand'],
  gray: ['gray', 'grey'],
  black: ['black'],
};

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    // VAEN: filter product media by selected tabletop color and, when available,
    // the selected leg/base finish. Size and other variant options are ignored.
    this.#filterVaenColorMedia(this, document);

    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.#filterVaenColorMedia(newMediaGallery, source);
    this.replaceWith(newMediaGallery);
  };

  /**
   * Normalizes Shopify option values to ALT tokens.
   * Examples: "Off White" -> "off_white", "Dark oak" -> "dark_oak".
   *
   * @param {string} value
   * @returns {string}
   */
  #normalizeVaenValue(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Returns the selected value for the first matching Shopify option name.
   * Supports both button/radio and dropdown variant pickers.
   *
   * @param {Element} picker
   * @param {string[]} optionNames
   * @returns {string|null}
   */
  #getVaenSelectedOption(picker, optionNames) {
    for (const optionName of optionNames) {
      const selectedRadio = [...picker.querySelectorAll('input[type="radio"]:checked')].find((input) => {
        const name = (input.getAttribute('name') || '').toLowerCase();
        return name.startsWith(`${optionName}-`) || name === `options[${optionName}]`;
      });

      if (selectedRadio?.value) return selectedRadio.value;

      const selectedSelect = [...picker.querySelectorAll('select')].find((select) => {
        const name = (select.getAttribute('name') || '').toLowerCase();
        return name === `options[${optionName}]` || name.startsWith(`${optionName}-`);
      });

      if (selectedSelect?.value) return selectedSelect.value;
    }

    return null;
  }

  /**
   * Finds the selected tabletop color and leg/base option for the current product.
   *
   * @param {Element} gallery
   * @param {Document|Element} root
   * @returns {{ topKeys: string[], legKey: string|null }|null}
   */
  #getVaenMediaSelection(gallery, root) {
    const section = gallery.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker') || root.querySelector('variant-picker');
    if (!picker) return null;

    const topValue = this.#getVaenSelectedOption(picker, [
      'tabletop color',
      'tabletop colour',
      'tabletop finish',
      'top color',
      'top colour',
      'top finish',
      'color',
      'colour',
    ]);

    if (!topValue) return null;

    const normalizedTop = this.#normalizeVaenValue(topValue);
    const topKeys = VAEN_COLOR_ALIASES[normalizedTop] || [normalizedTop];

    const legValue = this.#getVaenSelectedOption(picker, [
      'leg',
      'legs',
      'leg color',
      'leg colour',
      'leg finish',
      'base',
      'base color',
      'base colour',
      'base finish',
    ]);

    return {
      topKeys,
      legKey: legValue ? this.#normalizeVaenValue(legValue) : null,
    };
  }

  /**
   * Filters VAEN media using this priority:
   * 1) Exact TOP + LEG images, if that combination exists.
   * 2) TOP-only images as a fallback.
   * 3) Full gallery if neither group has been prepared yet.
   *
   * New ALT convention:
   *   product_top_<top-color>_leg_<leg-color>_<number>
   *   Example: reye_top_off_white_leg_black_01
   *
   * Existing TOP-only ALT convention remains supported:
   *   product_<top-color>_<number>
   *   Example: reye_off_white_01
   *
   * @param {Element} gallery
   * @param {Document|Element} root
   */
  #filterVaenColorMedia(gallery, root) {
    // The server filters all gallery surfaces before they are rendered.
    if (gallery.dataset.vaenServerGallery === 'true') return;
    const selection = this.#getVaenMediaSelection(gallery, root);
    if (!selection?.topKeys?.length) return;

    const getAlt = (element) => element.querySelector('img[alt]')?.getAttribute('alt')?.toLowerCase() || '';

    const topTokens = selection.topKeys.map((key) => `_${key}_`);
    const exactTokens = selection.legKey
      ? selection.topKeys.map((topKey) => `_top_${topKey}_leg_${selection.legKey}_`)
      : [];

    const matchesExact = (element) => {
      const alt = getAlt(element);
      return exactTokens.some((token) => alt.includes(token));
    };

    const matchesTopOnly = (element) => {
      const alt = getAlt(element);
      const isCombinationAlt = alt.includes('_top_') && alt.includes('_leg_');
      return !isCombinationAlt && topTokens.some((token) => alt.includes(token));
    };

    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const hasExactCombination = selection.legKey && slides.some(matchesExact);
    const hasTopOnly = slides.some(matchesTopOnly);

    let matchesMedia;
    if (hasExactCombination) {
      matchesMedia = matchesExact;
    } else if (hasTopOnly) {
      matchesMedia = matchesTopOnly;
    } else {
      // Never leave a product without images while media groups are still being prepared.
      return;
    }

    const filterByImageAlt = (selector) => {
      gallery.querySelectorAll(selector).forEach((element) => {
        if (!matchesMedia(element)) element.remove();
      });
    };

    filterByImageAlt('slideshow-slide');
    filterByImageAlt('.media-gallery__grid > li');
    filterByImageAlt('.dialog-thumbnails-list__thumbnail');
    filterByImageAlt('.dialog-zoomed-gallery > li');
    filterByImageAlt('.slideshow-controls__thumbnail');

    const filteredSlides = [...gallery.querySelectorAll('slideshow-slide')];
    const count = filteredSlides.length;

    filteredSlides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

      const style = slide.getAttribute('style');
      if (style) slide.setAttribute('style', style.replace(/--slide-\d+/g, `--slide-${index}`));

      this.#replaceIndexedAction(slide, 'on:click', /\/open\/\d+/, `/open/${index}`);
    });

    gallery.querySelectorAll('.media-gallery__grid > li').forEach((item, index) => {
      const zoomButton = [...item.querySelectorAll('*')].find((element) =>
        element.getAttribute('on:click')?.includes('/zoom/')
      );
      if (!zoomButton) return;

      this.#replaceIndexedAction(zoomButton, 'on:click', /\/zoom\/\d+/, `/zoom/${index}`);
      this.#replaceIndexedAction(zoomButton, 'on:pointerenter', /\/preloadImage\/\d+/, `/preloadImage/${index}`);
    });

    gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach((thumbnail, index) => {
      this.#replaceIndexedAction(thumbnail, 'on:click', /\/handleThumbnailClick\/\d+/, `/handleThumbnailClick/${index}`);
      this.#replaceIndexedAction(thumbnail, 'on:pointerenter', /\/preloadImage\/\d+/, `/preloadImage/${index}`);
      thumbnail.toggleAttribute('aria-selected', index === 0);
    });

    gallery.querySelectorAll('slideshow-controls').forEach((controls) => {
      const thumbnails = [...controls.querySelectorAll('.slideshow-controls__thumbnail')];
      thumbnails.forEach((thumbnail, index) => {
        this.#replaceIndexedAction(thumbnail, 'on:click', /\/select\/\d+/, `/select/${index}`);
        thumbnail.toggleAttribute('aria-selected', index === 0);
      });

      const dots = [...controls.querySelectorAll('.slideshow-controls__dots > li')];
      dots.slice(count).forEach((dot) => dot.remove());
      controls.querySelectorAll('.slideshow-controls__dots .slideshow-control').forEach((dot, index) => {
        this.#replaceIndexedAction(dot, 'on:click', /\/select\/\d+/, `/select/${index}`);
        dot.toggleAttribute('aria-selected', index === 0);
      });

      const counter = controls.querySelector('.slideshow-controls__counter');
      if (counter) counter.innerHTML = `<span ref="current">1</span><span class="slash">/</span>${count}`;
    });

    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow) {
      const timeline = Array.from({ length: count }, (_, index) => `--slide-${index}`).join(',');
      const style = slideshow.getAttribute('style') || '';
      slideshow.setAttribute('style', style.replace(/--slideshow-timeline:\s*[^;]*;/, `--slideshow-timeline: ${timeline};`));
      slideshow.setAttribute('initial-slide', '0');
    }

    if (count === 1) {
      gallery.querySelectorAll('slideshow-controls, slideshow-arrows, .slideshow-controls__arrows').forEach((element) => {
        element.remove();
      });
    }
  }

  /**
   * Rewrites a Fabric action attribute while preserving the rest of its route.
   *
   * @param {Element} element
   * @param {string} attribute
   * @param {RegExp} pattern
   * @param {string} replacement
   */
  #replaceIndexedAction(element, attribute, pattern, replacement) {
    const value = element.getAttribute(attribute);
    if (!value) return;
    element.setAttribute(attribute, value.replace(pattern, replacement));
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom media selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

